import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  graphQlClient,
  graphQlMultipleUpload,
} from '@config/graphqlClient';
import IncidentsGraphQL from '@graphql/incident.queries';
import { DelayUnitOfMeasure, Incident, IncidentType } from '@models/incident.model';
import { useTranslation } from '@hooks/useTranslation';
import { PENDING_INCIDENTS_QUERY_KEY } from './usePendingIncidents';

export interface CreateIncidentParams {
  name: string;
  projectId: string;
  incidentTime?: string;
  incidentType?: IncidentType | string;
  otherIncidentType?: string;
  delay?: number;
  delayUnitOfMeasure?: DelayUnitOfMeasure | string;
  description?: string;
  taskIds?: string[];
  files?: File[];
}

interface CreateIncidentResponse {
  createIncident: Incident;
}

// Field-for-field against `IncidentCreateInput`
// (backend/src/entities/incident/input/incident.create.input.ts), mirroring mobile's
// `CreateReportIncidentModal` optional-field-omission behavior: only send a field when it has a
// real value, never an empty string/array placeholder.
const buildIncidentData = (params: CreateIncidentParams): Record<string, unknown> => {
  const incidentData: Record<string, unknown> = {
    name: params.name,
    projectId: params.projectId,
  };

  if (params.incidentTime) {
    incidentData.incidentTime = params.incidentTime;
  }
  if (params.incidentType) {
    incidentData.incidentType = params.incidentType;
  }
  if (params.incidentType === IncidentType.OTHER && params.otherIncidentType) {
    incidentData.otherIncidentType = params.otherIncidentType;
  }
  if (params.delay !== undefined && params.delay !== null && !Number.isNaN(params.delay)) {
    incidentData.delay = params.delay;
  }
  if (params.delayUnitOfMeasure) {
    incidentData.delayUnitOfMeasure = params.delayUnitOfMeasure;
  }
  if (params.description && params.description.trim()) {
    incidentData.description = params.description;
  }
  if (params.taskIds && params.taskIds.length > 0) {
    incidentData.taskIds = params.taskIds;
  }

  return incidentData;
};

const createIncidentAPI = async (
  params: CreateIncidentParams,
): Promise<CreateIncidentResponse> => {
  const incidentData = buildIncidentData(params);

  if (params.files && params.files.length > 0) {
    return graphQlMultipleUpload(
      params.files,
      'incident',
      IncidentsGraphQL.mutations.createIncident,
      incidentData,
    );
  }

  return graphQlClient.request(IncidentsGraphQL.mutations.createIncident, {
    incident: incidentData,
  });
};

export const useCreateIncident = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();
  const t = useTranslation('incidents');

  const mutation = useMutation({
    mutationFn: createIncidentAPI,
    onSuccess: () => {
      toast.success(t?.success || tActionMessages.incidentReportedSuccessfully);
      // usePendingIncidents backs the /incidents list, the sidebar badge, AND the dashboard
      // block — one query key, one invalidation (verified: no separate badge query exists).
      queryClient.invalidateQueries({
        queryKey: [PENDING_INCIDENTS_QUERY_KEY],
        refetchType: 'active',
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.errors?.[0]?.message ||
          error?.message ||
          t?.error ||
          tActionMessages.failedToReportIncident,
      );
    },
  });

  return {
    createIncident: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
  };
};
